/**
 * A hotbar slot's cooldown, sampled once per animation frame while it runs —
 * without re-rendering React per frame.
 *
 * Before 2026-10-08 each slot called `setState` on every frame while its
 * ability or the global cooldown ran, so one cast re-rendered every filled slot
 * (Slot, Tooltip, icon, labels) once per frame for half a second, and any
 * ability on cooldown kept React committing at the display's refresh rate:
 * DJ's arena playtest logged `reactCommitsPerSec` equal to fps (70–90/s,
 * React 50–68 ms/s) while he cast movement abilities.
 *
 * Now the frame loop hands every frame's state to subscribers. A subscriber
 * re-renders only when what it shows changes (`useLiveCooldown` with a key:
 * the slot keys on the mode, the overlay on mode/number/pips), and the
 * continuous part (the sweep angle, the recast bar) is written straight to the
 * DOM by `CooldownOverlay`.
 */

import { useEffect, useLayoutEffect, useMemo, useReducer, useRef } from 'react';
import type { Ability } from '../../../../shared/CharacterTypes';
import { CooldownStore } from '../../../systems/player/CooldownStore';
import { slotCooldownState, type SlotCooldownState } from './cooldownDisplay';

/** The GCD the slot sweep shows, ms (matches the server's `gcdDuration`). */
export const GCD_SWEEP_MS = 500;

/** How often an idle slot looks for a new cooldown, ms (no render). */
export const IDLE_POLL_MS = 250;

export type LiveCooldownListener = (state: SlotCooldownState) => void;

export interface LiveCooldown {
  /** The state now (computed fresh from CooldownStore). */
  read(): SlotCooldownState;
  /** Called while cooling/recharging, and on discrete idle state changes. */
  subscribe(listener: LiveCooldownListener): () => void;
}

/**
 * One frame loop per slot: a 250 ms poll notices a new cooldown, then a rAF
 * loop runs until cooldown and charge recovery end (the last frame is delivered
 * so subscribers see completion). Runs only while someone is subscribed.
 */
export function createLiveCooldown(read: () => SlotCooldownState): LiveCooldown {
  const listeners = new Set<LiveCooldownListener>();
  let raf: number | undefined;
  let idle: ReturnType<typeof setInterval> | undefined;
  let previousKey = '';
  const key = (state: SlotCooldownState) => `${state.mode}|${state.charges}|${state.maxCharges}|${state.remainingMs > 0}`;
  const running = (state: SlotCooldownState) => state.mode !== 'ready'
    || (state.charges !== undefined && state.remainingMs > 0);
  const publish = (state: SlotCooldownState) => {
    previousKey = key(state);
    for (const listener of listeners) listener(state);
  };

  const frame = () => {
    const state = read();
    publish(state);
    raf = running(state) ? requestAnimationFrame(frame) : undefined;
  };
  const start = () => {
    idle = setInterval(() => {
      if (raf !== undefined) return;
      const state = read();
      if (key(state) !== previousKey) publish(state);
      if (running(state)) raf = requestAnimationFrame(frame);
    }, IDLE_POLL_MS);
    const state = read();
    previousKey = key(state);
    if (running(state)) raf = requestAnimationFrame(frame);
  };
  const stop = () => {
    if (idle !== undefined) clearInterval(idle);
    if (raf !== undefined) cancelAnimationFrame(raf);
    idle = undefined;
    raf = undefined;
  };

  return {
    read,
    subscribe(listener) {
      listeners.add(listener);
      if (listeners.size === 1) start();
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) stop();
      };
    },
  };
}

/** The live cooldown for a hotbar ability (null for an empty or item slot). */
export function useAbilityLiveCooldown(ability: Ability | undefined): LiveCooldown | null {
  const id = ability?.id;
  // Registry metadata cooldowns are ms (AbilityBuilder sets `cooldown = ms`).
  const cooldownMs = ability?.cooldown || 0;
  return useMemo(() => (id ? createLiveCooldown(() => slotCooldownState({
    now: Date.now(),
    cooldownExpiresAt: CooldownStore.getCooldown(id),
    cooldownMs,
    gcdStartedAt: CooldownStore.getGCDTime(),
    gcdMs: GCD_SWEEP_MS,
    recast: CooldownStore.getRecastWindow(id) ?? null,
    charges: CooldownStore.getCharges(id),
  })) : null), [id, cooldownMs]);
}

const READY: SlotCooldownState = { mode: 'ready', remainingMs: 0, fraction: 0, label: null, castsLeft: 0 };

/**
 * The state now, re-rendering the caller only when `keyOf(state)` changes.
 * `null` reads as ready.
 */
export function useLiveCooldown(
  live: LiveCooldown | null,
  keyOf: (state: SlotCooldownState) => string,
): SlotCooldownState {
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const keyOfRef = useRef(keyOf);
  keyOfRef.current = keyOf;
  const state = live ? live.read() : READY;
  const key = keyOf(state);
  const renderedKey = useRef(key);
  useLayoutEffect(() => { renderedKey.current = key; });
  useEffect(() => {
    if (!live) return undefined;
    return live.subscribe((s) => {
      if (keyOfRef.current(s) !== renderedKey.current) rerender();
    });
  }, [live]);
  return state;
}
